import {computed, nextTick, ref, shallowRef, watch} from 'vue';

// X199 (Grant 2026-10-27: "quickly flashes GUIDs of the players before loading their corresponding player names …
// not the first time I've seen this happen with select boxes"): the state behind TlAsyncSelect. A select whose model
// holds ids shows each id's title only once an item with that id is in hand; until then the id is PENDING (the field
// draws a skeleton in its place, never the id). Items come from the field's own list, from `known` (names the caller
// already has, e.g. a team's players) and from `fetchByIds` (a by-ids request for ids the list's first page misses).
// Once nothing is loading and the list has answered, an id still unmatched is UNAVAILABLE (a word, still never the id).

const pathGet = (item, key) => {
  if (typeof key === 'function') return key(item);
  if (item == null || typeof item !== 'object') return item;
  if (key == null) return item;
  if (key in item) return item[key];
  return String(key).split('.').reduce((o, k) => (o == null ? undefined : o[k]), item);
};

export const asyncSelectValueOf = (item, itemValue = 'value') => pathGet(item, itemValue);

export const normalizeSelectedIds = (value) => {
  if (value == null || value === '') return [];
  return (Array.isArray(value) ? value : [value]).filter(v => v != null && v !== '' && typeof v !== 'object');
};

export const ASYNC_SELECT_GHOST_CLASS = 'tl-async-select__ghost';

/**
 * @param {object} o getters: model, items, known, loading, fetchByIds, itemValue, itemTitle, returnObject, unavailableText
 */
export function useAsyncSelection(o) {
  const fetched = shallowRef([]);
  const fetching = ref(false);
  const seenLoading = ref(!!o.loading());
  const attempted = ref(new Set());

  watch(() => !!o.loading(), (now) => { if (now) seenLoading.value = true; });

  const valueKey = () => o.itemValue?.() ?? 'value';
  const titleKey = () => o.itemTitle?.() ?? 'title';

  const byValue = computed(() => {
    const map = new Map();
    const key = valueKey();
    for (const list of [fetched.value, o.known?.() ?? [], o.items() ?? []]) {
      for (const item of list ?? []) map.set(asyncSelectValueOf(item, key), item);
    }
    return map;
  });

  const selectedIds = computed(() => (o.returnObject?.() ? [] : normalizeSelectedIds(o.model())));
  const missingIds = computed(() => selectedIds.value.filter(id => !byValue.value.has(id)));

  const settled = computed(() => {
    if (o.loading() || fetching.value) return false;
    if ((o.items() ?? []).length || seenLoading.value) {
      // The list has answered; a by-ids fetch still owed for a missing id keeps it pending.
      return !(o.fetchByIds?.() && missingIds.value.some(id => !attempted.value.has(id)));
    }
    // Nothing listed and no load seen yet: settled only once a by-ids fetch has been tried for every missing id.
    return !!o.fetchByIds?.() && missingIds.value.every(id => attempted.value.has(id));
  });

  const pendingIds = computed(() => (settled.value ? [] : missingIds.value));
  const unavailableIds = computed(() => (settled.value ? missingIds.value : []));
  const busy = computed(() => missingIds.value.length > 0 && (!!o.loading() || fetching.value));

  const isPending = (id) => pendingIds.value.includes(id);
  const isUnavailable = (id) => unavailableIds.value.includes(id);

  // What the field is given: its list, then the caller's known/fetched items for selected ids the list lacks, then a
  // hidden stand-in per still-missing id (title '' or the unavailable word) so Vuetify never falls back to the id.
  const fieldItems = computed(() => {
    const list = o.items() ?? [];
    const vKey = valueKey();
    const tKey = titleKey();
    if (!selectedIds.value.length) return list;
    const inList = new Set(list.map(i => asyncSelectValueOf(i, vKey)));
    const extras = [];
    for (const id of selectedIds.value) {
      if (inList.has(id)) continue;
      const item = byValue.value.get(id);
      if (item !== undefined) {
        extras.push(item);
      } else if (typeof vKey === 'string' && typeof tKey === 'string') {
        extras.push({
          [vKey]: id,
          [tKey]: isUnavailable(id) ? (o.unavailableText?.() ?? 'Unavailable') : '',
          props: {class: ASYNC_SELECT_GHOST_CLASS},
        });
      }
    }
    return extras.length ? [...list, ...extras] : list;
  });

  const fetchMissing = async () => {
    const fetcher = o.fetchByIds?.();
    if (!fetcher || o.loading()) return;
    // Let a list request the caller starts in the same tick go first; it may bring the names anyway.
    await nextTick();
    if (o.loading()) return;
    const want = missingIds.value.filter(id => !attempted.value.has(id));
    if (!want.length) return;
    attempted.value = new Set([...attempted.value, ...want]);
    fetching.value = true;
    try {
      const got = await fetcher(want);
      if (Array.isArray(got) && got.length) {
        const key = valueKey();
        const have = new Set(fetched.value.map(i => asyncSelectValueOf(i, key)));
        fetched.value = [...fetched.value, ...got.filter(i => !have.has(asyncSelectValueOf(i, key)))];
      }
    } catch {
      /* a failed by-ids fetch leaves the id unavailable: never the id itself */
    } finally {
      fetching.value = false;
    }
  };

  watch(() => [missingIds.value.join('|'), !!o.loading()], fetchMissing, {immediate: true});

  return {selectedIds, missingIds, pendingIds, unavailableIds, settled, busy, fieldItems, isPending, isUnavailable, fetching};
}
